import React, { useMemo, useState } from 'react';
import { ChevronLeftIcon, ChevronRightIcon, DownloadIcon, SearchIcon, ShoppingBagIcon } from 'lucide-react';
type SkuSeed = {
  name: string;
  size: string;
  msrp: number;
  units: number;
  price: number;
  discount: number;
  imageUrl: string;
};
const skuSeeds: SkuSeed[] = [{
  name: 'Katherine Dress',
  size: 'Small',
  msrp: 598,
  units: 28,
  price: 162.59,
  discount: 73,
  imageUrl: 'https://cdn.magicpatterns.com/patterns/generated-images/ddedbd33-117d-4b67-ba5c-3af3385dbe0b.jpg'
}, {
  name: 'Katherine Dress',
  size: 'Medium',
  msrp: 598,
  units: 18,
  price: 168.23,
  discount: 72,
  imageUrl: 'https://cdn.magicpatterns.com/patterns/generated-images/ddedbd33-117d-4b67-ba5c-3af3385dbe0b.jpg'
}, {
  name: 'Sarah Bird Mini Dress',
  size: 'Small',
  msrp: 498,
  units: 23,
  price: 124.95,
  discount: 75,
  imageUrl: 'https://cdn.magicpatterns.com/patterns/generated-images/16fa4f2a-8577-49a4-901a-e63a54451ebd.jpg'
}, {
  name: 'Daisy Dress x Rainey Collaboration',
  size: 'Medium',
  msrp: 548,
  units: 16,
  price: 152.71,
  discount: 72,
  imageUrl: 'https://cdn.magicpatterns.com/patterns/generated-images/650d6552-e453-4201-8166-9316b1528a58.jpg'
}, {
  name: 'Katherine Dress',
  size: 'X-Small',
  msrp: 598,
  units: 12,
  price: 178.63,
  discount: 70,
  imageUrl: 'https://cdn.magicpatterns.com/patterns/generated-images/ddedbd33-117d-4b67-ba5c-3af3385dbe0b.jpg'
}, {
  name: 'Daisy Dress',
  size: 'Small',
  msrp: 568,
  units: 15,
  price: 139.37,
  discount: 75,
  imageUrl: 'https://cdn.magicpatterns.com/patterns/generated-images/650d6552-e453-4201-8166-9316b1528a58.jpg'
}, {
  name: 'Desirae Midi Dress',
  size: 'Small',
  msrp: 578,
  units: 12,
  price: 148.82,
  discount: 72,
  imageUrl: 'https://cdn.magicpatterns.com/patterns/generated-images/870c32c0-b80e-4da5-9260-ac098cc1a3bb.jpg'
}];
const PAGE_SIZE = 10;
const TOTAL_PAGES = 150;
function formatCurrency(value: number) {
  return value.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD'
  });
}
export function SalesBySku({
  selectedOwner
}: {
  selectedOwner: string;
}) {
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);
  const hasData = selectedOwner === 'Eddy';
  const allRows = useMemo(() => Array.from({
    length: PAGE_SIZE * TOTAL_PAGES
  }, (_, index) => {
    const seed = skuSeeds[index % skuSeeds.length];
    const cycle = Math.floor(index / skuSeeds.length);
    const units = Math.max(1, seed.units - cycle % 8);
    const price = Math.max(28, seed.price - cycle % 6 * 2.15);
    return {
      ...seed,
      id: index + 1,
      name: cycle > 0 ? `${seed.name} ${cycle + 1}` : seed.name,
      units,
      price,
      barcode: `642135${String(133057 + index).padStart(6, '0')}`,
      totalSales: units * price
    };
  }), []);
  const filteredRows = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    if (!normalizedQuery) return allRows;
    return allRows.filter(row => `${row.name} ${row.barcode}`.toLowerCase().includes(normalizedQuery));
  }, [allRows, query]);
  const pageCount = Math.max(1, Math.ceil(filteredRows.length / PAGE_SIZE));
  const currentPage = Math.min(page, pageCount);
  const visibleRows = filteredRows.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);
  const updateQuery = (value: string) => {
    setQuery(value);
    setPage(1);
  };
  return <section className="mt-5 overflow-hidden rounded-2xl border border-ink/10 bg-paper" aria-labelledby="sales-sku-heading" data-id="element-2177" data-mp="2lk47r6oqxgr6">
      <div className="flex flex-col gap-4 border-b border-ink/10 px-5 py-5 sm:px-6 lg:flex-row lg:items-center lg:justify-between" data-id="element-2178" data-mp="27kbgna9b4yio">
        <div className="flex items-center gap-2" data-id="element-2179" data-mp="2chuyek57rtfd">
          <ShoppingBagIcon className="h-4 w-4 text-[#BB5E40]" aria-hidden="true" data-id="element-2180" data-mp="1xo04qldmqaor" />
          <h2 id="sales-sku-heading" className="text-base font-semibold" data-id="element-2181" data-mp="22li7tv4ytvou">Sales by SKU</h2>
        </div>
        <button type="button" className="inline-flex w-fit items-center gap-2 rounded-full bg-[#BB5E40] px-3.5 py-2 text-xs font-semibold text-cream transition-colors hover:bg-[#94452E]" data-id="element-2182" data-mp="3s0pz147wtlwo">
          <DownloadIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-2183" data-mp="1wybgpmckqczu" />
          Download sales
        </button>
      </div>
      {hasData ? <>
          <div className="px-5 py-4 sm:px-6" data-id="element-2184" data-mp="1swzpxdhyrlq2">
            <label className="sr-only" htmlFor="sku-search" data-id="element-2185" data-mp="1nwlmy9v7z1yw">Search by product name or SKU</label>
            <div className="relative" data-id="element-2186" data-mp="1iyjwv4zdm2dx">
              <SearchIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink/40" aria-hidden="true" data-id="element-2187" data-mp="1ixigqm3i6ppc" />
              <input id="sku-search" type="search" value={query} onChange={event => updateQuery(event.target.value)} placeholder="Search by name or SKU" className="w-full rounded-xl border border-ink/10 bg-cream px-10 py-2.5 text-sm text-ink placeholder:text-ink/40 focus:border-[#BB5E40]/40 focus:outline-none focus:ring-2 focus:ring-[#BB5E40]/15" data-id="element-2188" data-mp="1988s5g7vbp5z" />
            </div>
          </div>
          <div className="overflow-x-auto" data-id="element-2189" data-mp="g5g3513h8y6l">
            <table className="w-full min-w-[1050px] text-left text-xs" data-id="element-2190" data-mp="g4eh61kxara3">
              <thead className="border-y border-ink/10 bg-cream/50 text-ink/50" data-id="element-2191" data-mp="l3zoaza8y5xw">
                <tr data-id="element-2192" data-mp="q3m0d5ec9g54">
                  <th scope="col" className="px-5 py-3 font-semibold sm:px-6" data-id="element-2193" data-mp="q32gvk91ijoz">Name</th>
                  <th scope="col" className="px-3 py-3 font-semibold" data-id="element-2194" data-mp="v2f4w8bndwt5">Condition</th>
                  <th scope="col" className="px-3 py-3 font-semibold" data-id="element-2195" data-mp="3tkcds5c92vgu">Brand</th>
                  <th scope="col" className="px-3 py-3 font-semibold" data-id="element-2196" data-mp="3ti804vojui3d">Barcode</th>
                  <th scope="col" className="px-3 py-3 text-right font-semibold" data-id="element-2197" data-mp="2ej8z5twk8xf">MSRP</th>
                  <th scope="col" className="px-3 py-3 text-right font-semibold" data-id="element-2198" data-mp="7cchw6mlbh1e">Units sold</th>
                  <th scope="col" className="px-3 py-3 text-right font-semibold" data-id="element-2199" data-mp="c4mchzynh5a0">Avg price</th>
                  <th scope="col" className="px-3 py-3 text-right font-semibold" data-id="element-2200" data-mp="3at1ottq34srl">Total sales</th>
                  <th scope="col" className="px-5 py-3 text-right font-semibold sm:px-6" data-id="element-2201" data-mp="3frw8exvvmlcr">Avg discount</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-ink/10" data-id="element-2202" data-mp="37pvk2ne7ajp8">
                {visibleRows.map(row => <tr key={row.id} data-id="element-2203" data-mp="3hi1lsou98fhm">
                    <th scope="row" className="px-5 py-2 text-left font-medium sm:px-6" data-id="element-2204" data-mp="3hib4rsgvtmdt">
                      <span className="flex items-center gap-2.5" data-id="element-2205" data-mp="3cioo191uho09">
                        <img src={row.imageUrl} alt="" className="h-8 w-8 rounded-md object-cover" data-id="element-2206" data-mp="3claxje8aqdl4" />
                        <span data-id="element-2207" data-mp="2juap8r2xmnnn">
                          <span className="block text-ink" data-id="element-2208" data-mp="2jv2n68k04xh3">{row.name}</span>
                          <span className="mt-0.5 block font-normal text-ink/50" data-id="element-2209" data-mp="2yra5127xtmt7">{row.size}</span>
                        </span>
                      </span>
                    </th>
                    <td className="px-3 py-2" data-id="element-2210" data-mp="2tuti2bch5ehp"><span className="inline-flex rounded-full bg-[#9B5CC8] px-2.5 py-1 font-semibold text-cream" data-id="element-2211" data-mp="e12ove5pswlq">Rescue</span></td>
                    <td className="px-3 py-2 text-ink/70" data-id="element-2212" data-mp="91g8ivulb08p">Eddy</td>
                    <td className="px-3 py-2 tabular-nums text-ink/70" data-id="element-2213" data-mp="gm0osxru8mt3">{row.barcode}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-ink/70" data-id="element-2214" data-mp="lktxmcw6jpmx">{formatCurrency(row.msrp)}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-ink/70" data-id="element-2215" data-mp="7lkjrjvg57m4">{row.units}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-ink/70" data-id="element-2216" data-mp="bm4gimc3y97q">{formatCurrency(row.price)}</td>
                    <td className="px-3 py-2 text-right font-semibold tabular-nums text-ink" data-id="element-2217" data-mp="zbytd3wffxrz">{formatCurrency(row.totalSales)}</td>
                    <td className="px-5 py-2 text-right tabular-nums text-ink/70 sm:px-6" data-id="element-2218" data-mp="14atnh1zuh8re">{row.discount}%</td>
                  </tr>)}
              </tbody>
            </table>
          </div>
          <div className="flex flex-col gap-3 border-t border-ink/10 px-5 py-3 text-xs text-ink/55 sm:flex-row sm:items-center sm:justify-between sm:px-6" data-id="element-2219" data-mp="ujp84169blw1">
            <span data-id="element-2220" data-mp="1n22w791sjthx">Showing {filteredRows.length ? `${(currentPage - 1) * PAGE_SIZE + 1}–${Math.min(currentPage * PAGE_SIZE, filteredRows.length)}` : '0'} of {filteredRows.length.toLocaleString('en-US')} sample SKUs</span>
            <div className="flex items-center gap-2 self-end sm:self-auto" data-id="element-2221" data-mp="tjonzaqjbyw9">
              <button type="button" onClick={() => setPage(current => Math.max(1, current - 1))} disabled={currentPage === 1} aria-label="Previous SKU page" className="rounded-md p-1 text-ink/55 hover:bg-cream disabled:cursor-not-allowed disabled:opacity-35" data-id="element-2222" data-mp="okkf6l7iw36g"><ChevronLeftIcon className="h-4 w-4" data-id="element-2223" data-mp="1h99huoo88gkb" /></button>
              <span data-id="element-2224" data-mp="1cbzlsq6c16ni"><strong className="font-semibold text-ink" data-id="element-2225" data-mp="1cbzmkse5paok">{currentPage}</strong> of {pageCount}</span>
              <button type="button" onClick={() => setPage(current => Math.min(pageCount, current + 1))} disabled={currentPage === pageCount} aria-label="Next SKU page" className="rounded-md p-1 text-ink/55 hover:bg-cream disabled:cursor-not-allowed disabled:opacity-35" data-id="element-2226" data-mp="17atoef1q8v7n"><ChevronRightIcon className="h-4 w-4" data-id="element-2227" data-mp="3n2zgt4vkwxxb" /></button>
            </div>
          </div>
        </> : <p className="px-6 py-10 text-center text-sm text-ink/55" data-id="element-2228" data-mp="22lgsulzrr3ld">Select an owner to view sales by SKU.</p>}
    </section>;
}